import { yupResolver } from '@hookform/resolvers/yup';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { useCallback, useEffect } from 'react';
import type { FieldPath, Resolver, UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type { TFunction } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { number, object, string, type SchemaOf } from 'yup';
import type { EditResourceFormValues } from '../components/resources-list/ResourcesList.model';
import {
  RESOURCE_TEXT_FIELD_MAX_LENGTH,
  RESOURCE_TEXT_FIELD_MIN_LENGTH,
} from '../components/resources-list/ResourcesList.constants';

function createEditResourceSchema(
  getFieldRequiredMessageCallback: (fieldPath?: FieldPath<EditResourceFormValues>) => string,
  t: TFunction,
): SchemaOf<EditResourceFormValues> {
  return object().shape({
    name: string()
      .trim()
      .required(getFieldRequiredMessageCallback('name'))
      .min(RESOURCE_TEXT_FIELD_MIN_LENGTH, t('common:error.minLength', { minLength: RESOURCE_TEXT_FIELD_MIN_LENGTH }))
      .max(RESOURCE_TEXT_FIELD_MAX_LENGTH, t('common:error.maxLength', { maxLength: RESOURCE_TEXT_FIELD_MAX_LENGTH })),
    description: string()
      .transform((value: unknown) => {
        if (typeof value === 'string') {
          const trimmed = value.trim();
          return trimmed;
        }

        return value;
      })
      .optional()
      .test('min', t('common:error.minLength', { minLength: RESOURCE_TEXT_FIELD_MIN_LENGTH }), (value) => {
        if (typeof value === 'string') {
          const trimmed = value.trim();
          return trimmed === '' ? true : trimmed.length >= RESOURCE_TEXT_FIELD_MIN_LENGTH;
        }

        return true;
      })
      .max(RESOURCE_TEXT_FIELD_MAX_LENGTH, t('common:error.maxLength', { maxLength: RESOURCE_TEXT_FIELD_MAX_LENGTH })),
    file: object().shape({
      filename: string().required(),
      mimeType: string().defined(),
      sizeBytes: number().required(),
    }),
  });
}

export interface UseEditResourceFormReturn {
  readonly form: UseFormReturn<EditResourceFormValues>;
  readonly reset: () => void;
}

export function useEditResourceForm(defaultValues?: EditResourceFormValues): UseEditResourceFormReturn {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.RESOURCES, ALL_LNGS_NAMESPACES.COMMON]);

  const getFieldRequiredMessage = useCallback(
    (_?: FieldPath<EditResourceFormValues>): string => {
      return t('error.required', { ns: ALL_LNGS_NAMESPACES.COMMON });
    },
    [t],
  );

  const form = useForm<EditResourceFormValues>({
    mode: 'onBlur',
    defaultValues,
    resolver: yupResolver(
      createEditResourceSchema(getFieldRequiredMessage, t),
    ) as unknown as Resolver<EditResourceFormValues>,
  });

  useEffect(() => form.reset(defaultValues), [defaultValues, form]);

  const reset = useCallback(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  return { form, reset };
}
